import { useMutation } from '@tanstack/react-query'
import queryClient from '@/queryClient'
import { DocumentPage, updateDocumentPage } from '@/lib/api/client'
import QUERY_KEYS from './queryKeys'
import { useToast } from '@/components/ui/use-toast'
import CustomError from '@/lib/api/CustomError'

// ---------------------------------------------------------------------------
// useToggleSubpageVisibility
// Adds or removes a subpage UUID from template_values.hiddenSubPages on the
// parent operation's DocumentPage.
// ---------------------------------------------------------------------------

export const useToggleSubpageVisibility = () => {
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({
      parentPageId,
      subPageId,
      parentPage,
    }: {
      parentPageId: string
      subPageId: string
      documentVersionId: string
      parentPage: DocumentPage
    }) => {
      const hiddenSubPages: string[] =
        (parentPage.template_values?.hiddenSubPages as string[] | undefined) ??
        []
      const isCurrentlyHidden = hiddenSubPages.includes(subPageId)
      const newHiddenSubPages = isCurrentlyHidden
        ? hiddenSubPages.filter((id) => id !== subPageId)
        : [...hiddenSubPages, subPageId]

      return updateDocumentPage(parentPageId, {
        name: parentPage.name,
        template_values: {
          ...parentPage.template_values,
          hiddenSubPages: newHiddenSubPages,
        },
        parent_id: (parentPage.parent as string | null) ?? null,
      })
    },

    onMutate: async ({
      parentPageId,
      subPageId,
      documentVersionId,
      parentPage,
    }) => {
      await queryClient.cancelQueries({
        queryKey: [QUERY_KEYS.DOCUMENT_PAGES, { documentVersionId }],
      })

      const previousPages = queryClient.getQueryData<DocumentPage[]>([
        QUERY_KEYS.DOCUMENT_PAGES,
        { documentVersionId },
      ])

      const hiddenSubPages: string[] =
        (parentPage.template_values?.hiddenSubPages as string[] | undefined) ??
        []
      const isCurrentlyHidden = hiddenSubPages.includes(subPageId)
      const newHiddenSubPages = isCurrentlyHidden
        ? hiddenSubPages.filter((id) => id !== subPageId)
        : [...hiddenSubPages, subPageId]

      queryClient.setQueryData<DocumentPage[]>(
        [QUERY_KEYS.DOCUMENT_PAGES, { documentVersionId }],
        (pages) =>
          pages?.map((p) =>
            p.id === parentPageId
              ? {
                  ...p,
                  template_values: {
                    ...p.template_values,
                    hiddenSubPages: newHiddenSubPages,
                  },
                }
              : p,
          ) as DocumentPage[],
      )

      return { previousPages }
    },

    onError: (error, { documentVersionId }, context) => {
      if (context?.previousPages) {
        queryClient.setQueryData(
          [QUERY_KEYS.DOCUMENT_PAGES, { documentVersionId }],
          context.previousPages,
        )
      }
      if (error instanceof CustomError) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        })
      }
    },

    onSettled: (_, __, { documentVersionId }) => {
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.DOCUMENT_PAGES, { documentVersionId }],
      })
    },
  })
}
